/** external */
import _ from "lodash";
/** internal */
import {
  getOrganizationResourceFromCache,
  getQuestionFromCacheV2,
} from "Worksheet/modules/WorksheetGraphqlHelpers";
import { getOrganizationResourceFromCacheV2 } from "QuestionBank/modules/QuestionBankGraphqlHelper";
import {
  FIELD_UID_TITLE,
  MODE_STUDENT_EVALUATE,
} from "Worksheet/modules/WorksheetConstants";
import { extractContentWithMediaFromHTML } from "Worksheet/modules/WorksheetUtils";

const isTitleFieldEmpty = title => {
  const titleFieldData = extractContentWithMediaFromHTML({
    htmlString: title,
  });

  return _.isEmpty(titleFieldData) || /^\s*$/.test(titleFieldData);
};

/**
 * Retrieves question data from cache based on provided parameters for Graph components
 *
 * @param {Object} params - The parameters for fetching question data
 * @param {string} params.rootResourceId - ID of the root resource
 * @param {boolean} params.isFromInternalTool - Flag indicating if request is from internal tool
 * @param {string} params.userType - Type of user (e.g., student, teacher)
 * @param {string} params.worksheetMode - Current worksheet mode
 * @param {string} params.questionId - ID of the specific question
 * @param {boolean} params.isQueriesV2Enabled - Flag indicating if V2 queries are enabled
 * @returns {Object} Question data from cache
 */
const getQuestionCacheData = ({
  rootResourceId,
  isFromInternalTool,
  userType,
  worksheetMode,
  questionId,
  isQueriesV2Enabled,
}) => {
  const showAssessmentTools = _.isEqual(worksheetMode, MODE_STUDENT_EVALUATE);

  if (isFromInternalTool) {
    //WORKSHEET_RESTRUCTURING: Update with new data once queriesV2 support is added in isFromInternalTool
    return getOrganizationResourceFromCacheV2(rootResourceId);
  } else if (isQueriesV2Enabled) {
    return getQuestionFromCacheV2({ id: questionId, userType });
  } else {
    // WORKSHEET_RESTRUCTURING: Remove else part
    return getOrganizationResourceFromCache(
      rootResourceId,
      true,
      userType,
      showAssessmentTools
    );
  }
};

/**
 * Extracts data fields from question cache data based on format
 *
 * @param {Object} params - The parameters for extracting data fields
 * @param {Object} params.questionCacheData - The cached question data
 * @param {boolean} params.isSubQuestion - Flag indicating if this is a sub-question
 * @param {string} params.questionId - ID of the specific question
 * @param {boolean} params.isQueriesV2Enabled - Flag indicating if V2 queries are enabled
 * @returns {Array} Array of data fields
 */
const extractDataFields = ({
  questionCacheData,
  isSubQuestion,
  questionId,
  isQueriesV2Enabled,
}) => {
  if (isQueriesV2Enabled) {
    // Handle questionV2 fragment format - flat structure
    return _.get(questionCacheData, "fields");
  } else {
    // WORKSHEET_RESTRUCTURING: Remove else part
    // Handle existing format - hierarchical structure
    return !isSubQuestion
      ? _.get(questionCacheData, "item.fields")
      : _.first(
          _.map(
            _.filter(
              _.map(_.get(questionCacheData, "childrenV2.edges")),
              obj => _.get(obj, "node.item.id") === questionId
            ),
            obj => _.get(obj, "node.item.fields", [])
          )
        );
  }
};

/**
 * Extracts question title from data fields
 *
 * @param {Object} params - The parameters for extracting question title
 * @param {Array} params.dataFields - Array of data fields
 * @param {Function} params.t - Translation function
 * @returns {string} Question title
 */
const extractQuestionTitle = ({ dataFields, t }) => {
  const titleField = _.get(
    _.find(dataFields, { uid: FIELD_UID_TITLE }),
    "value",
    ""
  );

  return isTitleFieldEmpty(titleField)
    ? t("worksheet:untitled_question")
    : titleField;
};

/**
 * Gets question data for Graph components
 *
 * @param {Object} params - The parameters for getting question data
 * @param {string} params.questionId - ID of the specific question
 * @param {boolean} params.isSubQuestion - Flag indicating if this is a sub-question
 * @param {string} params.subQuestionParentId - ID of the parent question for sub-questions
 * @param {string} params.resourceId - ID of the resource
 * @param {string} params.userType - Type of user
 * @param {string} params.worksheetMode - Current worksheet mode
 * @param {boolean} params.isFromInternalTool - Flag indicating if request is from internal tool
 * @param {boolean} params.isQueriesV2Enabled - Flag indicating if V2 queries are enabled
 * @param {Function} params.t - Translation function
 * @returns {Object} Object containing question title and related data
 */
export const getGraphQuestionData = ({
  questionId,
  isSubQuestion,
  subQuestionParentId,
  resourceId,
  userType,
  worksheetMode,
  isFromInternalTool,
  isQueriesV2Enabled,
  t,
}) => {
  const rootResourceId = isSubQuestion ? subQuestionParentId : resourceId;

  // Get question cache data
  const questionCacheData = getQuestionCacheData({
    rootResourceId,
    isFromInternalTool,
    userType,
    worksheetMode,
    questionId,
    isQueriesV2Enabled,
  });

  // Extract data fields
  const dataFields = extractDataFields({
    questionCacheData,
    isSubQuestion,
    questionId,
    isQueriesV2Enabled,
  });

  // Extract question title
  const questionTitle = extractQuestionTitle({
    dataFields,
    t,
  });

  return {
    questionTitle,
    questionCacheData,
    dataFields,
  };
};
